/**
 * Scene transitions: how a part is entered and how the story ends (docs/SKEL-STORY.md "Scene
 * transitions"). Pure: no pixi, no DOM, no clock — `transitionView.ts` draws what this computes.
 *
 * **Timing is story time.** A transition is `{dur, lead, hold}` seconds on the *incoming* scene's
 * own clock. A play-through starts that clock at `sceneT = −lead` (a pre-roll no cue fires in), so
 * the progress `x = sceneT + lead ∈ [0, dur]` is a pure function of the runner clock: it scales with
 * the playback speed, freezes on pause, and draws the same frames live and in the recorder. There is
 * no wall clock and no timer anywhere in a transition.
 *
 * - **Two-phase kinds** (`fade`, `iris`): with `a = (dur − hold) / 2`, the outgoing picture is
 *   covered over `[0, a)`, the screen stays covered for `hold` (a card, twinkles), and the new scene
 *   is uncovered over the last `a`. The first line (t = 0) is voiced on a covered frame (`lead ≥ a`).
 * - **One-phase kinds** (`dissolve`, `wipe`, `push`, `page_turn`, `zoom`, `clock_wipe`, `ripple`):
 *   the outgoing picture shows (dimmed, under a card) for `hold`, then `k = ss((x − hold)/(dur − hold))`
 *   carries it away over the live scene.
 * - **An ending** plays after the last `scene_end` hold: only the out half, then covered for `hold`.
 *
 * **Entries.** Only a play-through (`'play'`: "Next", or auto after a `scene_end` hold) plays the
 * authored transition; a bundle without one gets `LEGACY_FADE`, today's 400 + 400 ms dip to black
 * with the voice at black. `'start'` (the first tap, "Again") plays scene 0's opening from a solid
 * fill. `'jump'` (a seek, a scene button) is a cut.
 *
 * **The look** (`resolveLook`) is what the renderer can honestly draw. Reduced motion keeps the
 * timing and trades movement for a fade or a dissolve; a DOM-underlay video plate cannot be
 * snapshotted, so it fades through the veil; Canvas2D has no filters and no verified textured
 * polygons, so the kinds that need them fall back to ones that do not.
 */

import type { Bundle, SceneGrade, SceneTransition, TransitionCard, TransitionFocus, TransitionKind } from '@/schema/story'

export type { SceneGrade, SceneTransition, TransitionCard, TransitionFocus, TransitionKind }

/** How a scene was entered: a play-through, a jump (seek, scene button), or the story's start. */
export type SceneEntry = 'play' | 'jump' | 'start'
export type TransitionDir = 'left' | 'right' | 'up' | 'down'
export type IrisShape = 'circle' | 'star' | 'heart'
export type CoverColor = 'black' | 'white' | 'night' | 'paper'
/** `renderer.name` of the pixi renderer in use. */
export type RendererKind = 'webgl' | 'webgpu' | 'canvas'

export const TRANSITION_KINDS: readonly TransitionKind[] = [
  'cut', 'fade', 'dissolve', 'iris', 'wipe', 'push', 'page_turn', 'zoom', 'clock_wipe', 'ripple',
]
/** Kinds that cover, hold covered, then uncover (the rest carry the old picture away in one go). */
export const TWO_PHASE: ReadonlySet<TransitionKind> = new Set<TransitionKind>(['fade', 'iris'])

/** The schema's ceiling on `dur` (seconds). */
export const MAX_TRANSITION_S = 4
/** A play-through into a scene whose bundle names no transition: the old 400 + 400 ms black fade. */
export const LEGACY_FADE: Readonly<SceneTransition> = Object.freeze({ kind: 'fade', dur: 0.8, lead: 0.4 })
/** The covered colours (`color`). `night` is the navy of a sky after dark. */
export const COVER_RGB: Readonly<Record<CoverColor, number>> = Object.freeze({
  black: 0x000000,
  white: 0xffffff,
  night: 0x0b1030,
  paper: 0xf4ecdc,
})
/** A one-phase card dims the old picture by at most this much. */
export const MAX_DIM = 0.35
/** Iris radius at the covered end when the bundle names none: % of the stage height (≈ a head). */
export const DEFAULT_FOCUS_R = 12

/** Reduced motion keeps every duration and trades movement for a fade or a dissolve (C1). */
const REDUCED_MOTION: Partial<Record<TransitionKind, TransitionKind>> = {
  iris: 'fade',
  wipe: 'dissolve',
  push: 'dissolve',
  page_turn: 'dissolve',
  zoom: 'dissolve',
  clock_wipe: 'dissolve',
  ripple: 'dissolve',
}
/** Canvas2D: no filters (ripple), no verified textured polygons (page turn, clock wipe). */
const CANVAS_FALLBACK: Partial<Record<TransitionKind, TransitionKind>> = {
  ripple: 'dissolve',
  page_turn: 'push',
  clock_wipe: 'wipe',
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** Smoothstep on [0, 1], clamped: the one easing every transition uses. */
export function ss(v: number): number {
  const x = clamp(Number.isFinite(v) ? v : 0, 0, 1)
  return x * x * (3 - 2 * x)
}

/* ------------------------------------------------------------ the field */

/**
 * A transition as the player may trust it: a known `kind` (anything else is a `fade`), `dur` in
 * `[0, 4]`, `lead` and `hold` in `[0, dur]`. The rest passes through. The input object itself comes
 * back when it is already in shape, so `normalizeBundle` stays shallow and idempotent.
 */
export function normalizeTransition(raw: unknown): SceneTransition | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const t = raw as Record<string, unknown>
  const kind = typeof t.kind === 'string' && (TRANSITION_KINDS as readonly string[]).includes(t.kind) ? (t.kind as TransitionKind) : 'fade'
  const dur = isNum(t.dur) ? clamp(t.dur, 0, MAX_TRANSITION_S) : LEGACY_FADE.dur
  const lead = isNum(t.lead) ? clamp(t.lead, 0, dur) : dur / 2
  const hold = isNum(t.hold) ? clamp(t.hold, 0, dur) : undefined
  const same = kind === t.kind && dur === t.dur && lead === t.lead && (t.hold === undefined ? hold === undefined : hold === t.hold)
  if (same) return raw as SceneTransition
  const out: Record<string, unknown> = { ...t, kind, dur, lead }
  if (hold === undefined) delete out.hold
  else out.hold = hold
  return out as SceneTransition
}

/* --------------------------------------------------------------- timing */

/**
 * The transition scene `i` is entered with: none for a jump; for a play-through its own, else the
 * legacy fade (scene 0 has nothing to fade from); for the start, only one the bundle wrote.
 */
export function transitionFor(bundle: Pick<Bundle, 'scenes'>, i: number, entry: SceneEntry): SceneTransition | null {
  if (entry === 'jump') return null
  const scene = bundle.scenes[i]
  if (!scene) return null
  const own = scene.transition ?? null
  if (entry === 'play') return own ?? (i > 0 ? LEGACY_FADE : null)
  return own
}

/** The story's ending: the last scene's `ending`, played after its `scene_end` hold is released. */
export function endingFor(bundle: Pick<Bundle, 'scenes'>, i: number): SceneTransition | null {
  if (i !== bundle.scenes.length - 1) return null
  return bundle.scenes[i]?.ending ?? null
}

/** Seconds the runner plays before the scene's t = 0 (the pre-roll): `lead`, clamped to `[0, dur]`. */
export function prerollOf(tr: Pick<SceneTransition, 'kind' | 'dur' | 'lead'> | null | undefined): number {
  if (!tr || tr.kind === 'cut') return 0
  const dur = isNum(tr.dur) ? clamp(tr.dur, 0, MAX_TRANSITION_S) : 0
  return isNum(tr.lead) ? clamp(tr.lead, 0, dur) : 0
}

/* ----------------------------------------------------------------- look */

export interface LookOptions {
  reducedMotion?: boolean
  /** A snapshot of the outgoing picture exists (a play-through that captured one). */
  hasFrom?: boolean
  /**
   * The plate kind on either side (`plates.ts#PlateKind`): a `video_dom` underlay cannot be
   * snapshotted or moved, so any kind becomes a fade (the caller holds no snapshot of one).
   */
  plateKind?: string | null
  renderer?: RendererKind
  deviceClass?: 'phone' | 'tablet' | 'desktop'
  /** An `ending`: only the out half of a two-phase kind plays. */
  ending?: boolean
}

/** What the renderer draws: the authored transition, degraded to what this device can show. */
export interface Look {
  /** The kind that is drawn. */
  kind: TransitionKind
  /** The kind the bundle asked for (logs, the recorder). */
  authored: TransitionKind
  dur: number
  lead: number
  hold: number
  twoPhase: boolean
  dir: TransitionDir
  shape: IrisShape
  colorName: CoverColor
  /** 0xRRGGBB of the covered colour (and of the fill when there is no old picture). */
  color: number
  focusOut: TransitionFocus | null
  focusIn: TransitionFocus | null
  twinkle: boolean
  card: TransitionCard | null
  /** The old picture is a real snapshot; false = a solid fill of `color`. */
  hasFrom: boolean
  ending: boolean
  /** The ripple's displacement-filter resolution: half on every device, a quarter on a phone. */
  filterResolution: number
}

const DIRS: readonly TransitionDir[] = ['left', 'right', 'up', 'down']
const SHAPES: readonly IrisShape[] = ['circle', 'star', 'heart']

/** `tr` as this renderer draws it (C1 *Reduced motion*, *No old picture*; the device fallbacks), or null for none. */
export function resolveLook(tr: SceneTransition | null | undefined, opts: LookOptions = {}): Look | null {
  const n = normalizeTransition(tr)
  if (!n) return null
  let kind: TransitionKind = n.kind
  const hasFrom = opts.hasFrom === true
  if (opts.plateKind === 'video_dom' && kind !== 'cut') {
    // A DOM video sits behind the canvas, where SceneRoot's moves and a snapshot cannot reach it:
    // a veil is all that can be drawn. (The OLD picture is drawn whenever the caller holds one — a
    // video going out never is, one coming in lies under the canvas, which it fades off.)
    kind = 'fade'
  }
  if (opts.renderer === 'canvas') kind = CANVAS_FALLBACK[kind] ?? kind
  if (opts.reducedMotion) kind = REDUCED_MOTION[kind] ?? kind
  if (opts.ending && kind !== 'cut' && !TWO_PHASE.has(kind)) kind = 'fade'
  const card = n.card && typeof n.card.text === 'string' && n.card.text.length > 0 ? n.card : null
  // C1: a title card is drawn on paper when there is no old picture under it.
  const named = n.color && n.color in COVER_RGB ? n.color : 'black'
  const colorName: CoverColor = card?.style === 'title' && !hasFrom ? 'paper' : named
  return {
    kind,
    authored: n.kind,
    dur: n.dur,
    lead: n.lead,
    hold: n.hold ?? 0,
    twoPhase: TWO_PHASE.has(kind),
    dir: n.dir && DIRS.includes(n.dir) ? n.dir : 'left',
    shape: n.shape && SHAPES.includes(n.shape) ? n.shape : 'circle',
    colorName,
    color: COVER_RGB[colorName],
    focusOut: n.focus_out ?? null,
    focusIn: n.focus_in ?? null,
    twinkle: n.twinkle === true && !opts.reducedMotion,
    card,
    hasFrom,
    ending: opts.ending === true,
    filterResolution: opts.deviceClass === 'phone' ? 0.25 : 0.5,
  }
}

/* ------------------------------------------------------------- progress */

export type TransitionPart = 'out' | 'hold' | 'in' | 'single'

export interface TransitionProgress {
  /** Seconds into the transition, clamped to `[0, dur]`. */
  x: number
  /** `x / dur` (1 for a zero-length transition). */
  u: number
  part: TransitionPart
  /**
   * Eased progress inside the part: `out` 0 → 1 as the old picture is covered, `hold` 1, `in` 0 → 1
   * as the new scene is uncovered, `single` 0 → 1 as the old picture is carried away (0 under a card).
   */
  k: number
  /** Nothing left to draw: the new scene shows as it is. */
  done: boolean
}

type Timing = Pick<SceneTransition, 'kind' | 'dur' | 'lead' | 'hold'>

const DONE: Readonly<TransitionProgress> = Object.freeze({ x: 0, u: 1, part: 'in', k: 1, done: true })

/** Progress of an entry transition at raw scene time `sceneT` (negative during the pre-roll), per C1. */
export function progressOf(tr: Timing, sceneT: number): TransitionProgress {
  const dur = isNum(tr.dur) ? clamp(tr.dur, 0, MAX_TRANSITION_S) : 0
  if (tr.kind === 'cut' || !(dur > 0)) return { ...DONE, x: dur }
  const lead = isNum(tr.lead) ? clamp(tr.lead, 0, dur) : 0
  const hold = isNum(tr.hold) ? clamp(tr.hold, 0, dur) : 0
  const x = clamp((Number.isFinite(sceneT) ? sceneT : 0) + lead, 0, dur)
  if (x >= dur) return { x: dur, u: 1, part: TWO_PHASE.has(tr.kind) ? 'in' : 'single', k: 1, done: true }
  const u = x / dur
  if (TWO_PHASE.has(tr.kind)) {
    const a = (dur - hold) / 2
    if (x < a) return { x, u, part: 'out', k: ss(x / a), done: false }
    if (x < a + hold) return { x, u, part: 'hold', k: 1, done: false }
    return { x, u, part: 'in', k: a > 0 ? ss((x - a - hold) / a) : 1, done: false }
  }
  if (x < hold) return { x, u, part: 'single', k: 0, done: false }
  return { x, u, part: 'single', k: ss((x - hold) / (dur - hold)), done: false }
}

/**
 * Progress of the story's `ending` at raw scene time `sceneT` of the last scene (C1: `x = sceneT −
 * duration`): the out half over `[0, dur − hold)`, then covered for good. It is never `done`.
 */
export function endingProgressOf(tr: Timing, sceneT: number, duration: number): TransitionProgress {
  const dur = isNum(tr.dur) ? clamp(tr.dur, 0, MAX_TRANSITION_S) : 0
  const hold = isNum(tr.hold) ? clamp(tr.hold, 0, dur) : 0
  const x = clamp((Number.isFinite(sceneT) ? sceneT : 0) - duration, 0, dur)
  const a = dur - hold
  if (tr.kind === 'cut' || !(dur > 0)) return { x, u: 1, part: 'hold', k: 1, done: false }
  if (x < a) return { x, u: x / dur, part: 'out', k: ss(x / a), done: false }
  return { x, u: x / dur, part: 'hold', k: 1, done: false }
}

/* ------------------------------------------------------------- coverage */

/** Alpha of a fade's veil (and the covered share of a two-phase kind): 0 → 1 → 0. */
export function veilAlpha(p: TransitionProgress): number {
  if (p.done) return 0
  if (p.part === 'out') return p.k
  if (p.part === 'hold') return 1
  if (p.part === 'in') return 1 - p.k
  return 0
}

/** How closed the iris is: 0 = wide open (nothing covered), 1 = shut. */
export function irisClosure(p: TransitionProgress): number {
  return veilAlpha(p)
}

/** How much of the old picture (or the covered colour) still stands between the child and the new scene. */
export function coverageOf(look: Look, p: TransitionProgress): number {
  if (p.done) return 0
  return look.twoPhase || look.ending ? veilAlpha(p) : 1 - p.k
}

/**
 * The share of the new scene uncovered — what the speech bubbles fade with. A push or a zoom moves
 * `SceneRoot`, which the bubbles (projected through the camera) do not follow: they wait for the end.
 */
export function uncoveredOf(look: Look | null, p: TransitionProgress | null): number {
  if (!look || !p || p.done) return 1
  if (look.ending) return 1 - veilAlpha(p)
  if (look.twoPhase) return p.part === 'in' ? p.k : 0
  if (look.kind === 'push' || look.kind === 'zoom') return 0
  return p.k
}

/** How far a scene's `grade` has ramped in when it is entered by play-through (C1 *grade*). */
export function gradeRamp(look: Look | null, p: TransitionProgress | null): number {
  if (!look || !p || p.done || look.ending) return 1
  if (look.twoPhase) return p.part === 'in' ? p.k : 0
  return p.k
}

/** One-phase kinds: how much the old picture is dimmed (under a card during `hold`, then as it leaves). */
export function holdDim(look: Look, p: TransitionProgress): number {
  if (look.twoPhase || look.hold <= 0 || p.done) return 0
  if (p.x < look.hold) return MAX_DIM * ss(p.x / Math.min(0.3, look.hold))
  return MAX_DIM * (1 - p.k)
}

/** Alpha of the transition's card: shown while covered (two-phase) or over the old picture (one-phase). */
export function cardAlpha(look: Look, p: TransitionProgress): number {
  if (!look.card || look.hold <= 0 || p.done) return 0
  const r = clamp(look.hold / 3, 0.08, 0.3)
  if (look.twoPhase || look.ending) {
    const a = look.ending ? look.dur - look.hold : (look.dur - look.hold) / 2
    const up = ss((p.x - a) / r)
    // The ending's card stays: the story ends on it.
    return look.ending ? up : Math.min(up, ss((a + look.hold - p.x) / r))
  }
  const tail = Math.max(0.08, 0.3 * (look.dur - look.hold))
  return Math.min(ss(p.x / r), 1 - ss((p.x - look.hold) / tail))
}

/* ------------------------------------------------------------- geometry */

export interface Frame {
  w: number
  h: number
}
/** The logical stage (§8.1, `index.ts#STAGE_W/H`); everything below is in its pixels. */
export const STAGE_FRAME: Readonly<Frame> = Object.freeze({ w: 1920, h: 1080 })

export type Pt = [number, number]

/** Distance from `focus` to the frame corner farthest from it. */
export function farCorner(focus: readonly [number, number], frame: Frame = STAGE_FRAME): number {
  const dx = Math.max(focus[0], frame.w - focus[0])
  const dy = Math.max(focus[1], frame.h - focus[1])
  return Math.hypot(dx, dy)
}

/**
 * The iris hole's radius at `closure` (0 open → 1 shut, eased by the caller): the far-corner
 * distance when open (the frame is fully uncovered), `rPx` at the covered edge, where it snaps.
 */
export function irisRadius(closure: number, focus: readonly [number, number], frame: Frame = STAGE_FRAME, rPx: number = (DEFAULT_FOCUS_R / 100) * frame.h): number {
  const far = farCorner(focus, frame)
  const r = clamp(rPx, 0, far)
  return r + (far - r) * (1 - clamp(closure, 0, 1))
}

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** The part of the old picture a wipe still shows at `k`: cropped away toward `dir`. */
export function wipeRect(k: number, dir: TransitionDir, frame: Frame = STAGE_FRAME): Rect {
  const q = clamp(k, 0, 1)
  const { w, h } = frame
  switch (dir) {
    case 'right':
      return { x: w * q, y: 0, w: w * (1 - q), h }
    case 'up':
      return { x: 0, y: 0, w, h: h * (1 - q) }
    case 'down':
      return { x: 0, y: h * q, w, h: h * (1 - q) }
    default:
      return { x: 0, y: 0, w: w * (1 - q), h }
  }
}

/** A push: the old picture (`from`) and the live scene (`to`) slide together toward `dir`. */
export function pushOffsets(k: number, dir: TransitionDir, frame: Frame = STAGE_FRAME): { from: Pt; to: Pt } {
  const q = clamp(k, 0, 1)
  const { w, h } = frame
  switch (dir) {
    case 'right':
      return { from: [w * q, 0], to: [-w * (1 - q), 0] }
    case 'up':
      return { from: [0, -h * q], to: [0, h * (1 - q)] }
    case 'down':
      return { from: [0, h * q], to: [0, -h * (1 - q)] }
    default:
      return { from: [-w * q, 0], to: [w * (1 - q), 0] }
  }
}

/** Keep the side of the line `a → b` where `side > 0` (Sutherland–Hodgman, one plane). */
function clipByLine(poly: readonly Pt[], a: Pt, b: Pt): Pt[] {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const side = (p: Pt): number => dx * (p[1] - a[1]) - dy * (p[0] - a[0])
  const out: Pt[] = []
  for (let i = 0; i < poly.length; i += 1) {
    const cur = poly[i] as Pt
    const prev = poly[(i + poly.length - 1) % poly.length] as Pt
    const sc = side(cur)
    const sp = side(prev)
    if (sc >= 0) {
      if (sp < 0) out.push(lerpPt(prev, cur, sp / (sp - sc)))
      out.push(cur)
    } else if (sp >= 0) {
      out.push(lerpPt(prev, cur, sp / (sp - sc)))
    }
  }
  return out
}

const lerpPt = (a: Pt, b: Pt, t: number): Pt => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]

/** Area sign of a polygon (shoelace): positive = the clip rule's winding. */
function signedArea(poly: readonly Pt[]): number {
  let s = 0
  for (let i = 0; i < poly.length; i += 1) {
    const p = poly[i] as Pt
    const q = poly[(i + 1) % poly.length] as Pt
    s += p[0] * q[1] - q[0] * p[1]
  }
  return s / 2
}

/** `subject` ∩ the convex polygon `clip` (any winding). Empty when they do not overlap. */
export function clipConvex(subject: readonly Pt[], clip: readonly Pt[]): Pt[] {
  if (clip.length < 3 || subject.length < 3) return []
  const ring = signedArea(clip) < 0 ? [...clip].reverse() : [...clip]
  let out: Pt[] = [...subject]
  for (let i = 0; i < ring.length && out.length > 0; i += 1) {
    // `clipByLine` keeps `side > 0`, which for a positive (screen: clockwise) ring is the inside.
    out = clipByLine(out, ring[i] as Pt, ring[(i + 1) % ring.length] as Pt)
  }
  return out.length >= 3 ? out : []
}

export interface PageGeometry {
  /** The part of the old picture still lying flat (a polygon, empty once it has turned). */
  front: Pt[]
  /** The turned-over part of the page, its paper back up (a convex polygon, often empty). */
  flap: Pt[]
  /** The fold line, top → bottom in the page's own frame. */
  fold: [Pt, Pt]
  /** Unit vector from the fold toward the uncovered side (where the shadow falls on the new scene). */
  normal: Pt
}

/**
 * A page turned toward `dir` at `k`: the free edge (the right one for `left`) lifts along a slanted
 * fold that sweeps across the frame, its paper back folded over the part still lying flat, until
 * the whole page has gone. Pure in `(k, dir)`; the renderer fills `front` with the snapshot,
 * `flap` with paper and shades along `fold`.
 */
export function pageCorners(k: number, dir: TransitionDir, frame: Frame = STAGE_FRAME): PageGeometry {
  const q = clamp(k, 0, 1)
  const vertical = dir === 'up' || dir === 'down'
  // Worked in the frame of a `left` turn, then mapped: `up` / `down` swap the axes.
  const cw = vertical ? frame.h : frame.w
  const ch = vertical ? frame.w : frame.h
  const slant = 0.25 * ch
  const top: Pt = [cw - (cw + slant) * q, 0]
  const bottom: Pt = [cw + slant - (cw + slant) * q, ch]
  const rect: Pt[] = [[0, 0], [cw, 0], [cw, ch], [0, ch]]
  // `clipByLine` keeps the side of top → bottom with a positive cross product: the flat (left) side.
  const front = clipByLine(rect, top, bottom)
  const lifted = clipByLine(rect, bottom, top)
  const d: Pt = [bottom[0] - top[0], bottom[1] - top[1]]
  const len = Math.hypot(d[0], d[1]) || 1
  const u: Pt = [d[0] / len, d[1] / len]
  const reflect = (p: Pt): Pt => {
    const t = (p[0] - top[0]) * u[0] + (p[1] - top[1]) * u[1]
    const f: Pt = [top[0] + u[0] * t, top[1] + u[1] * t]
    return [2 * f[0] - p[0], 2 * f[1] - p[1]]
  }
  // a sliver (the fold passing through a corner) is nothing, not a zero-area triangle
  const solid = (poly: Pt[]): Pt[] => (poly.length >= 3 && Math.abs(signedArea(poly)) > 0.5 ? poly : [])
  const flap = solid(lifted).map(reflect)
  const normal: Pt = [u[1], -u[0]]
  const P = (p: Pt): Pt => {
    switch (dir) {
      case 'right':
        return [frame.w - p[0], p[1]]
      case 'up':
        return [p[1], p[0]]
      case 'down':
        return [p[1], frame.h - p[0]]
      default:
        return [p[0], p[1]]
    }
  }
  const V = (v: Pt): Pt => {
    switch (dir) {
      case 'right':
        return [-v[0], v[1]]
      case 'up':
        return [v[1], v[0]]
      case 'down':
        return [v[1], -v[0]]
      default:
        return [v[0], v[1]]
    }
  }
  return {
    front: solid(front).map(P),
    flap: flap.map(P),
    fold: [P(top), P(bottom)],
    normal: V(normal),
  }
}

/** A zoom-through: the old picture grows 1 → 2.5 and fades; the live scene settles 1.35 → 1. */
export function zoomScales(k: number): { from: number; fromAlpha: number; to: number } {
  const q = clamp(k, 0, 1)
  return { from: 1 + 1.5 * q, fromAlpha: 1 - q, to: 1.35 - 0.35 * q }
}

/** Radians the clock hand has swept, clockwise from 12 o'clock. */
export function clockAngle(k: number): number {
  return 2 * Math.PI * clamp(k, 0, 1)
}

/**
 * What rides the clock hand: 0 the sun, 1 the moon. When night falls (the entered scene is graded
 * `night`) the sun turns into the moon over the middle of the sweep; otherwise it stays the sun.
 */
export function clockMoon(k: number, night: boolean): number {
  return night ? ss((k - 0.35) / 0.3) : 0
}

/** The ripple's wave amplitude: 0 at both ends, 1 in the middle. */
export function rippleAmp(k: number): number {
  return Math.sin(Math.PI * clamp(k, 0, 1))
}

/* ------------------------------------------------------------ twinkles */

export interface Twinkle {
  x: number
  y: number
  /** Outer radius, stage px. */
  r: number
  alpha: number
}

export const TWINKLE_COUNT = 16

/** FNV-1a of a string: a stable seed (the story id and the scene index, say). */
export function seedOf(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Little stars over a covered moment: where they are comes from `seed`, how bright each is from
 * `x` (seconds into the transition) — pure, so a pause holds them and the recorder repeats them.
 * `envelope` (0-1) fades the whole sky with the cover.
 */
export function twinkles(seed: number, x: number, envelope = 1, n: number = TWINKLE_COUNT, frame: Frame = STAGE_FRAME): Twinkle[] {
  const rnd = mulberry32(seed)
  const env = clamp(Number.isFinite(envelope) ? envelope : 0, 0, 1)
  const out: Twinkle[] = []
  for (let i = 0; i < n; i += 1) {
    const px = (0.05 + 0.9 * rnd()) * frame.w
    const py = (0.05 + 0.7 * rnd()) * frame.h
    const r = 9 + 15 * rnd()
    const f = 0.7 + 1.5 * rnd()
    const ph = rnd()
    const glow = 0.5 + 0.5 * Math.sin(2 * Math.PI * (f * x + ph))
    out.push({ x: px, y: py, r, alpha: env * (0.3 + 0.7 * glow) })
  }
  return out
}

/* --------------------------------------------------------------- grade */

const GRADE_RGB: Readonly<Record<SceneGrade, readonly [number, number, number]>> = {
  dusk: [255, 186, 150],
  night: [112, 128, 196],
}

const toHex = (r: number, g: number, b: number): number => (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b)

/** The multiply tint of a graded scene at `strength` (0 = untouched white, 1 = the full grade). */
export function gradeTint(grade: SceneGrade | null | undefined, strength = 1): number {
  const rgb = grade ? GRADE_RGB[grade] : undefined
  if (!rgb) return 0xffffff
  const s = clamp(Number.isFinite(strength) ? strength : 0, 0, 1)
  return toHex(255 + (rgb[0] - 255) * s, 255 + (rgb[1] - 255) * s, 255 + (rgb[2] - 255) * s)
}

/** Linear mix of two 0xRRGGBB colours. */
export function mixTint(a: number, b: number, t: number): number {
  const q = clamp(Number.isFinite(t) ? t : 0, 0, 1)
  const ch = (c: number, s: number): number => (c >> s) & 0xff
  return toHex(
    ch(a, 16) + (ch(b, 16) - ch(a, 16)) * q,
    ch(a, 8) + (ch(b, 8) - ch(a, 8)) * q,
    ch(a, 0) + (ch(b, 0) - ch(a, 0)) * q,
  )
}
